import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  Input,
  OnInit,
  Directive,
  Renderer2,
  OnDestroy,
  ElementRef,
  ChangeDetectorRef,
} from '@angular/core';

import { ISnHasHostElement } from '@lib/common/interfaces';
import { mixinColor, mixinSize } from '@lib/common/mixins';
import { SnColorPalette } from '@lib/common/types';
import { SnComponentUpdater } from '@lib/core';

import { ISnSmartBadgeEvent } from '../interfaces/smart-badge-event.interface';
import { SnSmartBadgeService } from '../services/smart-badge.service';
import { SnBadgePositionX, SnBadgePositionY } from '../types/badge-position.type';

class SnBaseBadge implements ISnHasHostElement {
  public readonly hostElement: HTMLElement;

  constructor(elementRef: ElementRef<HTMLElement>) {
    this.hostElement = elementRef.nativeElement;
  }
}

export const SnBaseBadgeDirective = mixinSize(mixinColor(SnBaseBadge, { prefix: 'badge' }), {
  prefix: 'badge',
  defaultSize: 'md',
});

@Directive({
  selector: '[snBadge]',
  inputs: ['size: snBadgeSize'],
  host: {
    class: 'sn-badge',
    '[class.sn-badge--above]': 'isAbove',
    '[class.sn-badge--below]': 'isBelow',
    '[class.sn-badge--middle]': 'isMiddle',
    '[class.sn-badge--before]': 'isBefore',
    '[class.sn-badge--after]': 'isAfter',
    '[class.sn-badge--hidden]': 'isHidden',
    '[class.sn-badge--inline]': 'inline',
  },
})
export class SnBadgeDirective extends SnBaseBadgeDirective implements OnInit, OnDestroy {
  @Input('snBadge')
  set content(value: string | number) {
    this._updateContent(value);
  }

  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('snSmartBadge')
  set name(value: string) {
    if (this._name !== value) {
      this._name = value;
      this._updateContent(this._smartBadgeService.getBadge(this._name));
    }
  }

  @Input('snBadgeInline')
  set inline(value: boolean) {
    this._inline = coerceBooleanProperty(value);
  }
  get inline(): boolean {
    return this._inline;
  }

  @Input('snBadgePositionX')
  public positionX: SnBadgePositionX = 'after';

  @Input('snBadgePositionY')
  public positionY: SnBadgePositionY = 'above';

  @Input('snBadgeColor')
  public color: SnColorPalette;

  private _name: string;
  private _inline: boolean;
  private _hasContent: boolean;
  private _badgeElement: HTMLSpanElement;

  private readonly _destroy$ = new Subject();

  constructor(
    elementRef: ElementRef<HTMLElement>,
    private readonly _renderer: Renderer2,
    private readonly _componentUpdater: SnComponentUpdater<ISnSmartBadgeEvent>,
    private readonly _smartBadgeService: SnSmartBadgeService,
    private readonly _changeDetectorRef: ChangeDetectorRef,
  ) {
    super(elementRef);

    this._hasContent = false;
  }

  get isBelow(): boolean {
    return this.positionY === 'below';
  }
  get isMiddle(): boolean {
    return this.positionY === 'middle';
  }
  get isAbove(): boolean {
    return !this.isBelow && !this.isMiddle;
  }

  get isBefore(): boolean {
    return this.positionX === 'before';
  }
  get isAfter(): boolean {
    return !this.isBefore;
  }

  get isHidden(): boolean {
    return !this._hasContent;
  }

  public ngOnInit(): void {
    this._componentUpdater
      .componentUpdated$('snSmartBadge')
      .pipe(
        filter(() => !!this._name),
        filter(({ name }) => this._name === name),
        takeUntil(this._destroy$),
      )
      .subscribe(({ value, color }) => {
        this._updateContent(value, color);
        this._changeDetectorRef.markForCheck();
      });
  }

  public ngOnDestroy(): void {
    this._destroyBadgeElement();

    this._destroy$.next();
    this._destroy$.complete();
  }

  private _updateContent(value: string | number, color?: SnColorPalette): void {
    if (!this._badgeElement) {
      this._badgeElement = this._createBadgeElement();
    }

    const content = value && `${value}`.trim();

    this._hasContent = content && content.length > 0;
    this._badgeElement.textContent = content;
    if (color) {
      this._updateColor(color);
    }
  }

  private _updateColor(color: SnColorPalette): void {
    this.color = color;
  }

  private _createBadgeElement(): HTMLSpanElement {
    const badgeElement: HTMLSpanElement = this._renderer.createElement('span');
    badgeElement.classList.add('sn-badge__content');

    this.hostElement.appendChild(badgeElement);

    return badgeElement;
  }

  private _destroyBadgeElement(): void {
    if (this._badgeElement && this._renderer.destroyNode) {
      this._renderer.destroyNode(this._badgeElement);
      this._badgeElement = null;
    }
  }
}
